/**
 * Dialog 弹窗组件
 *
 * 依赖：
 * - jQuery 3.x
 * - useZIndex.js (层级管理，可选，没有时使用降级方案)
 *
 * 使用方法：
 * showDialog(options).then(function(){...}).catch(function(){...});
 *
 * 配置参数 options:
 * @param {String|DOM|jQuery} html - 弹窗内容
 * @param {String} className - 自定义样式类
 * @param {Number|String} width - 弹窗宽度
 * @param {Number|String} height - 弹窗高度
 * @param {String} title - 弹窗标题
 *
 * 头部插槽：
 * @param {String|DOM|jQuery} headerSlot - 完全自定义头部（除关闭按钮）
 * @param {String|DOM|jQuery} headerTitleSlot - 自定义标题区域
 * @param {String|DOM|jQuery} headerActionsSlot - 头部右侧操作区
 *
 * 底部插槽：
 * @param {String|DOM|jQuery} footerSlot - 完全自定义底部
 * @param {String|DOM|jQuery} footerActionsSlot - 底部左侧操作区
 * @param {String|DOM|jQuery}footerButtonSlot - 底部按钮区
 *
 * 显示控制：
 * @param {Boolean} showHeader - 是否显示头部
 * @param {Boolean}showFooter - 是否显示底部
 * @param {Boolean} showCloseIcon - 是否显示关闭图标
 * @param {Boolean} showOkButton - 是否显示确认按钮
 * @param {Boolean} showCancelButton - 是否显示取消按钮
 *
 * 按钮文本：
 * @param {String} okText - 确认按钮文本
 * @param {String}cancelText - 取消按钮文本
 *
 * 回调函数：
 * @param {Function} onMount - 挂载完成回调
 * @param {Function} onClose - 关闭回调
 */
(function (window, $) {
  "use strict";

  /**
   * Dialog 构造函数
   * @param {Object} options 配置参数
   */
  var Dialog = function (options) {
    this.options = $.extend(
      {
        html: "", // 弹窗内容
        className: "", // 自定义样式类
        width: 520, // 弹窗宽度
        height: "auto", // 弹窗高度
        title: "提示", // 标题
        headerSlot: "", // 头部栏插槽
        headerActionsSlot: "", // 头部右侧插槽
        headerTitleSlot: "", // 头部标题插槽
        footerSlot: "", // 底部插槽
        footerActionsSlot: "", // 底部左侧插槽
        footerButtonSlot: "", // 底部按钮插槽
        showHeader: true, // 是否显示头部
        showFooter: true, // 是否显示底部
        okText: "确定", // 确认按钮文本
        cancelText: "取消", // 取消按钮文本
        showCancelButton: true, // 是否显示取消按钮
        showOkButton: true, // 是否显示确认按钮
        onMount: null, // 挂载完成回调
        onClose: null, // 关闭回调
        showCloseIcon: true, // 是否显示关闭图标
        showLogo: false, // 是否显示 logo
        beforeOk: null, // 确认按钮点击前的回调
        beforeCancel: null, // 取消按钮点击前的回调
      },
      options,
    );

    this.$dialog = null;
    this.$overlay = null;
    this.resolve = null;
    this.reject = null;
    this.isClosing = false;
    this.zIndex = null; // 存储层级信息
  };

  Dialog.prototype = {
    constructor: Dialog,

    /**
     * 初始化并显示弹窗
     * @returns {Promise}
     */
    show: function () {
      var self = this;

      return new Promise(function (resolve, reject) {
        self.resolve = resolve;
        self.reject = reject;
        self.render();
        self.bindEvents();
        self.mount();
      });
    },

    /**
     * 渲染弹窗 DOM
     */
    render: function () {
      var options = this.options;

      // 创建遮罩层
      this.$overlay = $('<div class="dialog-overlay"></div>');

      // 创建弹窗容器
      var dialogClass = "dialog-container " + (options.className || "");
      this.$dialog = $('<div class="' + dialogClass + '"></div>');

      // 设置宽度和高度
      var styles = {
        width: typeof options.width === "number" ? options.width + "px" : options.width,
      };
      if (options.height !== "auto") {
        styles.height = typeof options.height === "number" ? options.height + "px" : options.height;
      }
      this.$dialog.css(styles);

      var contentParts = [];

      // ========== 构建头部 ==========
      if (options.showHeader) {
        var headerHtml = "";

        // 如果有完全自定义的头部插槽
        if (options.headerSlot) {
          headerHtml += '<div class="dialog-header">';
          headerHtml += '<div class="dialog-header-slot"></div>';
          if (options.showCloseIcon) {
            headerHtml += '<i class="iconfont icon-close1 dialog-close"></i>';
          }
          headerHtml += "</div>";
        } else {
          // 否则使用默认头部结构
          headerHtml += '<div class="dialog-header">';

          // 标题区域
          if (options.headerTitleSlot) {
            headerHtml += '<div class="dialog-header-title-slot"></div>';
          } else {
            headerHtml += '<h3 class="dialog-title">' + options.title + "</h3>";
          }

          // 右侧操作区域
          if (options.headerActionsSlot) {
            headerHtml += '<div class="dialog-header-actions-slot"></div>';
          }

          if (options.showLogo) {
            headerHtml +=
              '<div class="dialog-header-logo">\
              <img src="/static/Public/images/new_home/41.png" alt="Logo" />\
            </div>';
          }

          // 关闭按钮
          if (options.showCloseIcon) {
            headerHtml += '<i class="iconfont icon-close1 dialog-close"></i>';
          }

          headerHtml += "</div>";
        }

        contentParts.push(headerHtml);
      }

      // ========== 构建内容区 ==========
      var bodyHtml = '<div class="dialog-body">';
      bodyHtml += '<div class="dialog-content"></div>';
      bodyHtml += "</div>";
      contentParts.push(bodyHtml);

      // ========== 构建底部 ==========
      if (options.showFooter) {
        var footerHtml = "";

        // 如果有完全自定义的底部插槽
        if (options.footerSlot) {
          footerHtml += '<div class="dialog-footer">';
          footerHtml += '<div class="dialog-footer-slot"></div>';
          footerHtml += "</div>";
        } else {
          // 否则使用默认底部结构
          footerHtml += '<div class="dialog-footer">';

          // 左侧操作区域
          if (options.footerActionsSlot) {
            footerHtml += '<div class="dialog-footer-actions-slot"></div>';
          }

          // 右侧按钮区域
          if (options.footerButtonSlot) {
            footerHtml += '<div class="dialog-footer-button-slot"></div>';
          } else {
            // 默认按钮
            footerHtml += '<div class="dialog-footer-buttons">';
            if (options.showCancelButton) {
              footerHtml +=
                '<button class="dialog-btn dialog-btn-cancel">' + options.cancelText + "</button>";
            }
            if (options.showOkButton) {
              footerHtml +=
                '<button class="dialog-btn dialog-btn-ok">' + options.okText + "</button>";
            }
            footerHtml += "</div>";
          }

          footerHtml += "</div>";
        }

        contentParts.push(footerHtml);
      }

      // 组装弹窗
      this.$dialog.html(contentParts.join(""));

      // ========== 插入内容 ==========
      this.insertContent(".dialog-content", options.html);

      // ========== 插入头部插槽 ==========
      if (options.headerSlot) {
        this.insertContent(".dialog-header-slot", options.headerSlot);
      } else {
        if (options.headerTitleSlot) {
          this.insertContent(".dialog-header-title-slot", options.headerTitleSlot);
        }
        if (options.headerActionsSlot) {
          this.insertContent(".dialog-header-actions-slot", options.headerActionsSlot);
        }
      }

      // ========== 插入底部插槽 ==========
      if (options.footerSlot) {
        this.insertContent(".dialog-footer-slot", options.footerSlot);
      } else {
        if (options.footerActionsSlot) {
          this.insertContent(".dialog-footer-actions-slot", options.footerActionsSlot);
        }
        if (options.footerButtonSlot) {
          this.insertContent(".dialog-footer-button-slot", options.footerButtonSlot);
        }
      }
    },

    /**
     * 插入内容的通用方法
     * @param {String} selector 选择器
     * @param {String|jQuery|DOM} content 内容
     */
    insertContent: function (selector, content) {
      if (!content) return;

      var $target = this.$dialog.find(selector);
      if ($target.length === 0) return;

      if (typeof content === "string") {
        $target.html(content);
      } else if (content instanceof jQuery) {
        $target.append(content);
      } else if (content && content.nodeType) {
        $target.append($(content));
      }
    },

    /**
     * 绑定事件
     */
    bindEvents: function () {
      var self = this;

      // 关闭按钮
      this.$dialog.on("click", ".dialog-close", function () {
        self.cancel();
      });

      // 取消按钮
      this.$dialog.on("click", ".dialog-btn-cancel", async function () {
        if (typeof self.options.beforeCancel === "function" && !(await self.options.beforeCancel()))
          return;
        self.cancel();
      });

      // 确认按钮
      this.$dialog.on("click", ".dialog-btn-ok", async function () {
        if (typeof self.options.beforeOk === "function" && !(await self.options.beforeOk())) return;
        self.confirm();
      });

      // 点击遮罩层关闭
      this.$overlay.on("click", function (e) {
        if (e.target === self.$overlay[0]) {
          self.cancel();
        }
      });

      // ESC 键关闭
      $(document).on("keydown.dialog", function (e) {
        if (e.keyCode === 27) {
          self.cancel();
        }
      });
    },

    /**
     * 挂载到页面
     */
    mount: function () {
      var self = this;

      // 使用 useZIndex 获取层级
      if (window.useZIndex) {
        this.zIndex = window.useZIndex("dialog");
      } else {
        // 降级方案：使用固定层级
        this.zIndex = {
          overlay: 9998,
          content: 9999,
        };
      }

      // 设置层级
      this.$overlay.css("z-index", this.zIndex.overlay);
      this.$dialog.css("z-index", this.zIndex.content);

      // 添加到 body
      $("body").append(this.$overlay).append(this.$dialog);

      // 添加显示动画
      setTimeout(function () {
        self.$overlay.addClass("dialog-overlay-show");
        self.$dialog.addClass("dialog-show");
      }, 10);

      // 触发挂载回调
      if (typeof this.options.onMount === "function") {
        this.options.onMount.call(this);
      }
    },

    /**
     * 确认操作
     */
    confirm: function () {
      if (this.isClosing) return;

      this.isClosing = true;

      if (this.resolve) {
        this.resolve();
      }
      this.close();
    },

    /**
     * 取消操作
     */
    cancel: function () {
      if (this.isClosing) return;

      this.isClosing = true;

      if (this.reject) {
        this.reject();
      }
      this.close();
    },

    /**
     * 关闭弹窗
     */
    close: function () {
      var self = this;

      // 移除动画类
      this.$overlay.removeClass("dialog-overlay-show");
      this.$dialog.removeClass("dialog-show");

      // 等待动画完成后移除 DOM
      setTimeout(function () {
        self.destroy();
      }, 300);

      // 触发关闭回调
      if (typeof this.options.onClose === "function") {
        this.options.onClose.call(this);
      }
    },

    /**
     * 销毁弹窗
     */
    destroy: function () {
      // 移除键盘事件
      $(document).off("keydown.dialog");

      // 移除 DOM
      if (this.$overlay) {
        this.$overlay.remove();
        this.$overlay = null;
      }
      if (this.$dialog) {
        this.$dialog.remove();
        this.$dialog = null;
      }
    },
  };

  /**
   * 显示弹窗的便捷方法
   * @param {Object} options 配置参数
   * @returns {Promise}
   */
  function showDialog(options) {
    var dialog = new Dialog(options);
    return dialog.show();
  }

  // 暴露到全局
  window.Dialog = Dialog;
  window.showDialog = showDialog;
})(window, jQuery);
